import React from 'react';
import { injectIntl, type WrappedComponentProps } from 'react-intl';
import { IconButton } from '@eventbrite/legacy-eds.icon-button';
import { LogoTwitter } from '@eventbrite/legacy-eds.iconography';
import type { UtmOptions } from './types';
import { openUrl } from './utils';
import { TWITTER_AFFILIATED_CODE } from './constants';

const TWITTER_URL = 'https://twitter.com/intent/tweet';

interface TwitterShareProps {
    resourceId: string;
    resourceName: string;
    resourceUrl: string;
    resourceType: string;
    twitterHandle: string;
    onClick?: () => void;
    utmOptions: UtmOptions;
    affiliateChannel?: string;
    dataHeapId?: string;
    shareMethod?: string;
    dataPageArea?: string;
    className?: string;
}

const _getResourceUrl = (
    resourceUrl: string,
    affiliateChannel: string | undefined,
    utmOptions: UtmOptions,
) => {
    const url = new URL(resourceUrl);
    const affCode = affiliateChannel
        ? TWITTER_AFFILIATED_CODE[affiliateChannel]
        : TWITTER_AFFILIATED_CODE['default'];
    url.searchParams.append('aff', affCode);
    Object.entries(utmOptions).forEach(([key, value]) => {
        url.searchParams.append(key, value);
    });
    return url.toString();
};

class TwitterShareComponent extends React.Component<
    TwitterShareProps & WrappedComponentProps<'intl'>
> {
    _handleButtonClick() {
        const {
            affiliateChannel,
            intl,
            resourceName,
            resourceUrl,
            onClick,
            twitterHandle,
            utmOptions,
        } = this.props;
        const text = intl.formatMessage(
            {
                id: 'Check out "{resourceName}" {resourceUrl} @{twitterHandle}',
                defaultMessage: 'Check out "{resourceName}" {resourceUrl} @{twitterHandle}',
            },
            {
                resourceName,
                twitterHandle,
                resourceUrl: _getResourceUrl(resourceUrl, affiliateChannel, utmOptions),
            },
        );
        const url = new URL(TWITTER_URL);
        url.searchParams.append('text', text);

        if (onClick) {
            onClick();
        }

        openUrl(url.toString());
    }

    render() {
        const { intl } = this.props;
        return (
            <span
                className={this.props.className}
                data-testid="twitter-share-box-container"
                data-heap-id={this.props.dataHeapId}
                data-share-method={this.props.shareMethod}
                data-page-area={this.props.dataPageArea}
                {...{
                    [`data-${this.props.resourceType}-id`]: this.props.resourceId,
                }}
            >
                <IconButton
                    iconType={<LogoTwitter />}
                    title={intl.formatMessage({
                        id: 'Share on Twitter',
                        defaultMessage: 'Share on Twitter',
                    })}
                    onClick={this._handleButtonClick.bind(this)}
                />
            </span>
        );
    }
}

export const TwitterShare = injectIntl(TwitterShareComponent);
